import type { MaterialListItem, MaterialReceipt } from "@edu/shared";
import { externalCallHost } from "@edu/shared";

/**
 * Слова для ссылок и файлов, отправленных ученику: состояние квитанции и
 * подпись источника. Проект `docs/material-receipt-design.md`, разделы 6 и 14.
 *
 * Отдельным модулем с тестами, а не ветками в разметке, по той же причине, что
 * `student-skills-text.ts`: здесь правило проще всего нарушить незаметно.
 *
 * Три правила, и все три замки, а не стиль:
 *
 * 1. **Безлично.** Поля пола в схеме нет, поэтому «Открыт», а не «Петя
 *    открыл»: иначе половина учеников получает «Маша открыл» (раздел 14);
 * 2. **Слов «посмотрел» и «досмотрел» нет ни на одном уровне** (раздел 6.2):
 *    отметку ставит открытие, а не просмотр;
 * 3. **Ни одного счёта в адрес преподавателя** («отправлено 12, открыто 3»):
 *    считается ученик, а не тот, кто смотрит на экран (раздел 14, решение
 *    владельца 23.08.2026). Поэтому функции здесь берут ОДНУ квитанцию, а не
 *    список.
 *
 * ⚠️ **Четвёртое состояние раздела 6 («Открывали: 4, имён нет») здесь не
 * считается, потому что его нечем посчитать:** `copied_at` и `anon_opens` это
 * часть Б, и в контракте части А их нет ни одного поля. Правило «пустая
 * квитанция не пишется словами „не открыт“, если ссылку копировали наружу» при
 * этом соблюдено по построению: копировать наружу в части А нечем. В день части
 * Б сюда приезжают два входа, и «Не открыт» обязан перестать быть ответом по
 * умолчанию.
 */

const dayMonth = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });
const dayMonthYear = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * Дата квитанции словами: «14 сентября», с годом только когда он не текущий.
 * Месяц полным словом, как в проекте, а не «14 сент.» строк списка: это не
 * столбец дат, а часть фразы.
 */
function openedDay(ts: number, now: number): string {
  const sameYear = new Date(ts).getFullYear() === new Date(now).getFullYear();
  // `Intl` в `ru-RU` с годом пишет «14 сентября 2025 г.», а внутри фразы
  // сокращение лишнее
  return (sameYear ? dayMonth : dayMonthYear).format(new Date(ts)).replace(/\s*г\.$/, "");
}

/**
 * Состояние квитанции одного ученика.
 *
 * `undefined` это «строки получателя нет в ответе». Сервер такого сегодня не
 * отдаёт (список собирается по строкам получателя), но слова на этот случай
 * выбраны заранее и выбраны осторожные: «не открыт» про то, чего мы не знаем,
 * это ровно то уверенное обвинение, которое раздел 6 запрещает печатать.
 */
export function receiptText(receipt: MaterialReceipt | undefined, now: number): string {
  if (receipt === undefined) return "Сведений нет";
  if (receipt.firstOpenedAt === null) return "Не открыт";
  return `Открыт ${openedDay(receipt.firstOpenedAt, now)}`;
}

/**
 * Докуда дошло воспроизведение файла, второй строкой под отметкой.
 *
 * ⚠️ **Формулировка из раздела 6.2 проекта: есть «открыт» и «примерно
 * половина», и ничего сильнее.** Веху шлёт плеер ученика, а перемотка в конец
 * ставит 100 за три секунды, поэтому слов «посмотрел» и «досмотрел» здесь нет
 * ни в одной ветке, а «примерно» стоит у каждой, включая конец: «до конца» без
 * «примерно» читалось бы как «досмотрел».
 *
 * Корзина печатается словом, а не процентом и не временем: «до 4:12» у
 * дошедшего до 49% это недоговорка на четыре минуты уверенным тоном.
 *
 * `null` это «вехи нет», и тогда не печатается ничего: у ссылки вехи не бывает
 * по построению, а у файла пустота значит «не включали или не дошли до
 * четверти», и сказать об этом нечего, что не было бы догадкой.
 */
export function milestoneText(milestone: MaterialReceipt["milestone"] | undefined): string | null {
  switch (milestone) {
    case 25:
      return "примерно четверть";
    case 50:
      return "примерно половина";
    case 75:
      return "примерно три четверти";
    case 100:
      return "примерно до конца";
    default:
      return null;
  }
}

/** Пометка отзыва второй строкой под названием: «отозван 20 сентября». Тем же родом, что «Открыт». */
export function revokedText(revokedAt: number, now: number): string {
  return `отозван ${openedDay(revokedAt, now)}`;
}

/**
 * Откуда материал, второй строкой под названием.
 *
 * ⚠️ У ссылки это ДОМЕН, и показывается он всегда (раздел 8 проекта): адрес ввёл
 * один человек, а откроет другой, часто несовершеннолетний. Домен достаёт
 * `externalCallHost`, та же функция, что у ссылки на созвон, а не своя.
 *
 * ⚠️ У файла пустой `fileId` это ЗАКОННОЕ состояние, а не поломка: файл стёрт
 * из медиатеки, а квитанция пережила его намеренно (раздел 5 проекта).
 */
export function materialSourceText(item: Pick<MaterialListItem, "kind" | "url" | "fileId">): string {
  if (item.kind === "link") {
    const host = externalCallHost(item.url);
    // Адрес в базе старше сегодняшних проверок: неразбираемый не показываем
    // ссылкой вовсе, а говорим об этом словами
    return host === null ? "ссылка с ошибкой" : host;
  }
  return item.fileId === null ? "файл удалён из материалов" : "файл из материалов";
}

/**
 * Название по умолчанию для файла: его имя без расширения. «Урок 5.mp4» в
 * заголовке у ученика читается как имя файла, а не как название материала.
 * Точку в начале имени («.mp4») расширением не считаем, иначе название пустое.
 */
export function titleFromFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  return base.trim();
}
